import React from 'react';
import { AdminRideDetailsDto } from '../api/client';

declare const mapboxgl: any;

type RouteLayer = {
  id: string;
  label: string;
  color: string;
  dash?: number[];
  coordinates: number[][];
};

function validCoordinates(coordinates?: number[][] | null) {
  return (coordinates || []).filter((point) => Array.isArray(point) && point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]));
}

export function AdminRideRouteMap({ ride }: { ride: AdminRideDetailsDto }) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<any>(null);
  const token = (process.env.REACT_APP_MAPBOX_TOKEN || '').trim();

  const layers = React.useMemo<RouteLayer[]>(() => {
    const routeLayers: RouteLayer[] = [];
    const planned = validCoordinates(ride.plannedRouteCoordinates);
    const actualDriver = validCoordinates(ride.actualRouteCoordinates);
    if (planned.length > 1) {
      routeLayers.push({ id: 'planned', label: 'Planned driver route', color: '#2563eb', dash: [2, 2], coordinates: planned });
    }
    if (actualDriver.length > 1) {
      routeLayers.push({ id: 'driver-actual', label: 'Actual driver route', color: '#111827', coordinates: actualDriver });
    }
    ride.bookings.forEach((booking, index) => {
      const actualRider = validCoordinates(booking.actualRouteCoordinates);
      if (actualRider.length > 1) {
        routeLayers.push({
          id: `rider-${index}`,
          label: `Rider route ${booking.rider?.email || booking.rider?.id || index + 1}`,
          color: ['#059669', '#dc2626', '#7c3aed', '#ea580c'][index % 4],
          coordinates: actualRider,
        });
      }
    });
    return routeLayers;
  }, [ride]);

  React.useEffect(() => {
    if (!token || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = token;
    const center = validCoordinates(ride.plannedRouteCoordinates)[0]
      || validCoordinates(ride.actualRouteCoordinates)[0]
      || [ride.departure.longitude, ride.departure.latitude];
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center,
      zoom: 10,
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [ride.actualRouteCoordinates, ride.departure.latitude, ride.departure.longitude, ride.plannedRouteCoordinates, token]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const draw = () => {
      layers.forEach((layer) => {
        const layerId = `admin-${layer.id}`;
        const sourceId = `${layerId}-source`;
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: layer.coordinates },
            properties: {},
          },
        });
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': layer.color,
            'line-width': layer.id === 'planned' ? 4 : 5,
            'line-opacity': layer.id === 'planned' ? 0.75 : 0.95,
            ...(layer.dash ? { 'line-dasharray': layer.dash } : {}),
          },
        });
      });

      const allCoordinates = layers.flatMap((layer) => layer.coordinates);
      if (allCoordinates.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        allCoordinates.forEach((point) => bounds.extend(point as [number, number]));
        map.fitBounds(bounds, { padding: 48, duration: 0 });
      }
    };

    if (map.loaded()) draw();
    else map.once('load', draw);
  }, [layers]);

  if (!token) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Missing REACT_APP_MAPBOX_TOKEN env var. Route coordinates are available, but the map cannot render.
      </div>
    );
  }

  if (layers.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
        No planned or actual route geometry is stored for this ride yet.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap gap-3 border-b px-4 py-3 text-xs font-semibold text-gray-600">
        {layers.map((layer) => (
          <span key={layer.id} className="inline-flex items-center gap-2">
            <span className="h-1.5 w-6 rounded-full" style={{ backgroundColor: layer.color }}></span>
            {layer.label}
          </span>
        ))}
      </div>
      <div ref={containerRef} className="h-[420px] w-full"></div>
    </div>
  );
}
